import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import logger from "../../../utils/logger";
import Community from "../../../models/Community";
import { addRecordToTop, updateRecord } from "../../../utils/dbUtils";
import { getCommunityById, getCommunityBySlug } from "../../../utils/entityUtils";
import { createSelector } from "reselect";
import CommunityThread from "../../../models/CommunityThread";
import CommunityMember from "../../../models/CommunityMember";

const initialState = {
    isLoading: false,
    error: null,
    communities: [],
    userCommunities: [],
    activeCommunityId: null,
    activeCommunityThreads: []
};

const communitiesReducer = (state = initialState, action) => {
    const { type, payload } = action;
    switch (type) {
        case "COMMUNITIES_FOUND":
            return {
                ...state,
                communities: payload.records,
                isLoading: false,
                error: null
            };
        case "USER_COMMUNITIES_FOUND":
            return {
                ...state,
                userCommunities: payload.records,
                isLoading: false,
                error: null
            };
        case "IS_LOADING":
            return {
                ...state,
                isLoading: true,
                error: null
            };
        case "ERROR":
            return {
                ...state,
                isLoading: false,
                error: payload.error
            };
        case 'CHANGE_ACTIVE_COMMUNITY':
            return {
                ...state,
                activeCommunityId: payload.communityId
            };
        case 'LOAD_ACTIVE_COMMUNITY_THREADS':
            return {
                ...state,
                activeCommunityThreads: payload.records
            };
        default:
            return state;
    }
};

export const getCommunities = () => {
    return async (dispatch) => {
        try {
            const records = await Community.getAll();
            dispatch({ type: "COMMUNITIES_FOUND", payload: { records: records } });
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
            throw error;
        }
    }
};

export const getUserCommunities = () => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const authState = { ...state.authState };
            const { currentAuthUserId } = authState;

            if (!currentAuthUserId) {
                dispatch({ type: "USER_COMMUNITIES_FOUND", payload: { records: [] } });
                return [];
            }

            const records = await Community.getByMemberId(currentAuthUserId);

            dispatch({ type: "USER_COMMUNITIES_FOUND", payload: { records: records } });

            return records;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const createCommunity = (newCommunity) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const authState = { ...state.authState };
            const { currentAuthUserId } = authState;

            if (!currentAuthUserId) {
                return false;
            }

            const newEntity = await new Community(newCommunity).create();

            const updatedCommunities = updateRecord('id', { ...newEntity }, [...state.communitiesState.communities]);
            dispatch({ type: "COMMUNITIES_FOUND", payload: { records: updatedCommunities } });

            const updatedUserCommunities = updateRecord('id', { ...newEntity }, [...state.communitiesState.userCommunities]);
            dispatch({ type: "USER_COMMUNITIES_FOUND", payload: { records: updatedUserCommunities } });

            return true;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return false;
    }
};

export const joinCommunity = (communityId) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const authState = { ...state.authState };
            const { currentAuthUserId } = authState;
            const communitiesState = { ...state.communitiesState };
            const { communities } = communitiesState;

            if (!currentAuthUserId) {
                return false;
            }

            const community = communities.find(c => c.id === communityId);
            if (!community) {
                return false;
            }

            if (community.privacy === 'PUBLIC') {
                const success = await CommunityMember.create(communityId, currentAuthUserId, 'ACTIVE');
                if (success) {
                    const updatedCommunities = updateRecord('id', { ...community, memberIds: [...community.memberIds, currentAuthUserId] }, [...state.communitiesState.communities]);
                    dispatch({ type: "COMMUNITIES_FOUND", payload: { records: updatedCommunities } });

                    const updatedUserCommunities = updateRecord('id', { ...community, memberIds: [...community.memberIds, currentAuthUserId] }, [...state.communitiesState.userCommunities]);
                    dispatch({ type: "USER_COMMUNITIES_FOUND", payload: { records: updatedUserCommunities } });

                    return true;
                }
            } else {
                const success = await CommunityMember.create(communityId, currentAuthUserId, 'PENDING');
                if (success) {
                    const updatedCommunities = updateRecord('id', { ...community, pendingMemberIds: [...community.pendingMemberIds, currentAuthUserId] }, [...state.communitiesState.communities]);
                    dispatch({ type: "COMMUNITIES_FOUND", payload: { records: updatedCommunities } });

                    const updatedUserCommunities = updateRecord('id', { ...community, pendingMemberIds: [...community.pendingMemberIds, currentAuthUserId] }, [...state.communitiesState.userCommunities]);
                    dispatch({ type: "USER_COMMUNITIES_FOUND", payload: { records: updatedUserCommunities } });

                    return true;
                }
            }
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return false;
    }
};

export const leaveCommunity = (communityId) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const authState = { ...state.authState };
            const { currentAuthUserId } = authState;
            const communitiesState = { ...state.communitiesState };
            const { communities } = communitiesState;

            if (!currentAuthUserId) {
                return false;
            }

            const community = communities.find(c => c.id === communityId);
            if (!community) {
                return false;
            }

            const success = await CommunityMember.updateStatus(communityId, currentAuthUserId, 'INACTIVE', currentAuthUserId);
            if (success) {
                const updatedCommunity = await Community.getById(communityId);

                const updatedCommunities = updateRecord('id', { ...updatedCommunity }, [...state.communitiesState.communities]);
                dispatch({ type: "COMMUNITIES_FOUND", payload: { records: updatedCommunities } });

                const updatedUserCommunities = updateRecord('id', { ...updatedCommunity }, [...state.communitiesState.userCommunities]);
                dispatch({ type: "USER_COMMUNITIES_FOUND", payload: { records: updatedUserCommunities } });

                return true;
            }
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return false;
    }
};

export const changeActiveCommunity = (communityId) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const communitiesState = { ...state.communitiesState };
            const { userCommunities } = communitiesState;

            const community = userCommunities.find(c => c.id === communityId);
            if (!community) {
                return null;
            }

            dispatch({ type: "CHANGE_ACTIVE_COMMUNITY", payload: { communityId: communityId } });

            return community;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return null;
    }
};

export const getCommunityThreads = (communityId) => {
    return async (dispatch) => {
        try {
            const records = await CommunityThread.getByCommunityId(communityId);
            dispatch({ type: "LOAD_ACTIVE_COMMUNITY_THREADS", payload: { records: records } });

            return records;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return [];
    }
};

export const createCommunityThread = (communityId, newThread) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            const newEntity = await new CommunityThread({ ...newThread, communityId: communityId }).create();

            const updatedThreads = addRecordToTop('id', { ...newEntity }, [...state.communitiesState.activeCommunityThreads]);
            dispatch({ type: "LOAD_ACTIVE_COMMUNITY_THREADS", payload: { records: updatedThreads } });
            dispatch({ type: "CHANGE_ACTIVE_COMMUNITY", payload: { communityId: communityId } });

            return newEntity;
        } catch (error) {
            logger.logReduxErrorEvent(error, getErrorMessage(error), true);
        }

        return null;
    }
};

// module scope so reselect memoization survives across renders, and each keys on the collection it
// reads - the whole slice gets a new identity on every dispatch and recomputed both of these
const selectCommunityById = createSelector((state) => state.communitiesState.communities, (_, id) => id, (communities, id) => getCommunityById(id, communities));
const selectCommunityBySlug = createSelector((state) => state.communitiesState.communities, (_, slug) => slug, (communities, slug) => getCommunityBySlug(slug, communities));

export const useCommunity = () => {
    const dispatch = useDispatch();

    return {
        communities: useSelector((appState) => appState.communitiesState.communities),
        userCommunities: useSelector((appState) => appState.communitiesState.userCommunities),
        isLoading: useSelector((appState) => appState.communitiesState.isLoading),
        error: useSelector((appState) => appState.communitiesState.error),

        fetchCommunities: () => dispatch(getCommunities()),
        fetchUserCommunities: () => dispatch(getUserCommunities()),

        createCommunity: (newCommunity) => dispatch(createCommunity(newCommunity)),
        joinCommunity: (communityId) => dispatch(joinCommunity(communityId)),
        leaveCommunity: (communityId) => dispatch(leaveCommunity(communityId)),

        selectCommunityById,
        selectCommunityBySlug,

        changeActiveCommunity: (communityId) => dispatch(changeActiveCommunity(communityId)),
        getCommunityThreads: (communityId) => dispatch(getCommunityThreads(communityId)),
        createCommunityThread: (communityId, newThread) => dispatch(createCommunityThread(communityId, newThread)),
    };
};

export default communitiesReducer;